import React from 'react';

import clsx from 'clsx';

/**
 * Action component - A small button/link with an icon
 * Automatically renders as a link if href is provided
 *
 * @example
 * ```tsx
 * <Action label="Download" icon={IconDownload} onClick={handleDownload} />
 * <Action label="View" icon={IconEye} href="/some-url" />
 * ```
 */

interface ActionProps {
  label: string;
  icon: React.ComponentType<any>;
  disabled?: boolean;
  className?: string;
  onClick?: React.MouseEventHandler;
  href?: string;
  [key: string]: any;
}

export const Action: React.FC<ActionProps> = ({
  label,
  icon: Icon,
  disabled,
  className,
  onClick,
  href,
  ...rest
}) => {
  const classes = clsx(
    'text-xs font-medium',
    'bg-[#231e27] text-white border border-accent-muted rounded px-2 h-6',
    'flex items-center gap-1.5',
    'transition-colors duration-200',
    '[&_svg]:w-4 [&_svg]:h-4 [&_svg_path]:fill-brand-700',
    !disabled && 'hover:bg-accent-muted cursor-pointer',
    disabled && [
      'text-input-border-disabled',
      'border-input-border-disabled',
      'bg-transparent',
      '[&_svg_path]:fill-input-border-disabled',
      'cursor-not-allowed',
      'hover:bg-transparent',
    ],
    className,
  );

  if (href && !disabled) {
    return (
      <a className={classes} href={href} onClick={onClick} {...rest}>
        {label}
        <Icon />
      </a>
    );
  }

  return (
    <button className={classes} disabled={disabled} onClick={onClick} {...rest}>
      {label}
      <Icon />
    </button>
  );
};
